import type { AgenticIntegration } from "@p0security/integrations-agentic-shared/components";
import { installedIds } from "@p0security/shared/install/installed";
import { useIntegration } from "hooks/useIntegration";
import { useObjectMemo } from "hooks/useObjectMemo";
import { createContext, useContext, useMemo } from "react";

import type { AgenticInstalls } from "./delegateIdentity";
import { EMPTY_AGENTIC_INSTALLS, toAgenticInstalls } from "./delegateIdentity";
import type { OauthServer } from "./oauthRegistration";
import { toOauthServers } from "./oauthRegistration";

/** Installed MCP servers a client can register an OAuth credential for. */
type OauthServersState = {
  error?: string;
  loading: boolean;
  servers: OauthServer[];
};

const EMPTY_OAUTH_SERVERS: OauthServersState = { loading: false, servers: [] };

type AgenticGatewayState = {
  hasGateway: boolean;
  installs: AgenticInstalls;
  loading: boolean;
  oauthServers: OauthServersState;
};

const AgenticGatewayContext = createContext<AgenticGatewayState | undefined>(
  undefined
);

/** Provides the tenant's installed agentic-gateway state to app navigation and routes. */
export const AgenticGatewayProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { doc, error, loading } = useIntegration<AgenticIntegration>(
    "agentic",
    { live: true }
  );
  const hasGateway = useMemo(
    () =>
      doc?.data !== undefined && installedIds("gateway", doc.data).length > 0,
    [doc?.data]
  );
  const installs = useMemo(() => toAgenticInstalls(doc?.data), [doc?.data]);
  const servers = useMemo(
    () => (doc?.data ? toOauthServers(doc.data) : []),
    [doc?.data]
  );
  const oauthServers = useObjectMemo({
    error: error ? "MCP servers could not be loaded. Try again." : undefined,
    loading,
    servers,
  });
  const state = useObjectMemo({ hasGateway, installs, loading, oauthServers });

  return (
    <AgenticGatewayContext.Provider value={state}>
      {children}
    </AgenticGatewayContext.Provider>
  );
};

/** Returns whether the active tenant has an installed agentic gateway. */
export const useAgenticGateway = (): AgenticGatewayState => {
  const state = useContext(AgenticGatewayContext);
  if (!state) {
    throw new Error(
      "useAgenticGateway must be used within an AgenticGatewayProvider"
    );
  }
  return state;
};

/** Returns the installed agentic components a delegation chain links to. */
export const useAgenticInstalls = (): AgenticInstalls =>
  useContext(AgenticGatewayContext)?.installs ?? EMPTY_AGENTIC_INSTALLS;

/** Returns the installed MCP servers a client can register an OAuth credential
 * for. */
export const useOauthServers = (): OauthServersState =>
  useContext(AgenticGatewayContext)?.oauthServers ?? EMPTY_OAUTH_SERVERS;
